/**
 * Search — one box over everything.
 *
 * ── The client does not sanitise, and that is deliberate ─────────────────────
 *
 * `search.ftsQuery()` on the server owns the whole of turning what somebody
 * typed into an FTS5 expression, and this screen sends the RAW string. Two
 * sanitisers is one sanitiser that drifts, and the one that drifts is always
 * the copy nobody is testing. `"`, `*`, `NEAR`, an empty string and a
 * five-hundred-character paste all reach the endpoint and all come back 200
 * with a well-formed body — so there is nothing here to guard against.
 *
 * ── Phone numbers ───────────────────────────────────────────────────────────
 *
 * `(787) 555-1234` tokenises to three short numbers, none of which matches a
 * row indexed as one ten-digit string. The server adds the concatenated digits
 * as an extra clause, so `(787) 555-1234`, `+1 787-555-1234` and `7875551234`
 * all reach the same seller. The one thing this screen must not do is strip or
 * reformat what was typed on the way out — that would take the punctuation the
 * server uses to recognise a phone number and throw it away.
 *
 * ── A degraded index says so ────────────────────────────────────────────────
 *
 * `strategy` comes back as `fts` or `like`. The LIKE fallback exists for one
 * state — a restored database whose `search_fts` has not been rebuilt — and it
 * has no ranking and no diacritic folding. A search that quietly returns worse
 * results with a straight face is worse than one that is visibly degraded, so
 * the fallback is labelled.
 *
 * ── §AG, §10.3 ───────────────────────────────────────────────────────────────
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { post } from '../lib/api'
import { useLocale, type Locale } from '../lib/i18n'
import { navigate } from '../lib/route'
import * as oppCache from '../lib/oppCache'
import Icon, { type IconName } from '../components/Icon'

/* ────────────────────────────────────────────────────────────────────────────
 * Server shapes
 * ────────────────────────────────────────────────────────────────────────── */

type EntityKind = 'opportunity' | 'listing' | 'note' | 'seller' | 'comm'

interface SearchHit {
  kind: EntityKind
  id: string
  opportunityId: string | null
  title: string
  /** `snippet()` output, with `[` and `]` around the matched terms. */
  excerpt: string
  rank: number
}

interface SearchResponse {
  query: string
  expression: string | null
  strategy: 'fts' | 'like' | 'empty'
  hits: SearchHit[]
  groups: Record<string, SearchHit[]>
}

/* ────────────────────────────────────────────────────────────────────────────
 * Copy
 * ────────────────────────────────────────────────────────────────────────── */

const EN = {
  title: 'Search',
  placeholder: 'Title, seller, phone, VIN, place, note…',
  hint: 'Searches listings, sellers, phone numbers, notes, conversations and VIN or serial numbers.',
  clear: 'Clear',
  searching: 'Searching…',
  none: 'Nothing matched.',
  noneWhy: 'Try fewer words, or part of a phone number.',
  failed: 'The search could not run.',
  degraded: 'The search index is being rebuilt, so these results are unranked.',
  recent: 'Recently opened',
  recentWhy: 'These are cached on this device and open with no signal.',
  kind_opportunity: 'Opportunities',
  kind_listing: 'Listings',
  kind_seller: 'Sellers',
  kind_note: 'Notes',
  kind_comm: 'Conversations',
  results: 'results',
} as const

type CopyKey = keyof typeof EN

const ES: Record<CopyKey, string> = {
  title: 'Buscar',
  placeholder: 'Título, vendedor, teléfono, VIN, lugar, nota…',
  hint: 'Busca en anuncios, vendedores, teléfonos, notas, conversaciones y números de VIN o serie.',
  clear: 'Limpiar',
  searching: 'Buscando…',
  none: 'No encontramos nada.',
  noneWhy: 'Prueba con menos palabras, o con parte de un teléfono.',
  failed: 'La búsqueda no pudo correr.',
  degraded: 'El índice de búsqueda se está reconstruyendo, así que estos resultados no están ordenados.',
  recent: 'Abiertas hace poco',
  recentWhy: 'Estas están guardadas en este equipo y abren sin señal.',
  kind_opportunity: 'Oportunidades',
  kind_listing: 'Anuncios',
  kind_seller: 'Vendedores',
  kind_note: 'Notas',
  kind_comm: 'Conversaciones',
  results: 'resultados',
}

const COPY: Record<Locale, Record<CopyKey, string>> = { en: EN, 'es-PR': ES }

/* ────────────────────────────────────────────────────────────────────────────
 * Presentation
 * ────────────────────────────────────────────────────────────────────────── */

const KIND_ORDER: EntityKind[] = ['opportunity', 'listing', 'seller', 'note', 'comm']

const KIND_ICON: Record<EntityKind, IconName> = {
  opportunity: 'home',
  listing: 'ticket',
  seller: 'person',
  note: 'door',
  comm: 'phone',
}

const S = {
  page: { padding: 12, paddingBottom: 96, maxWidth: 720, margin: '0 auto' } as const,
  bar: { display: 'flex', gap: 8, alignItems: 'stretch' } as const,
  input: {
    flex: 1,
    minWidth: 0,
    minHeight: 52,
    fontSize: 17,
    padding: '12px 14px',
    borderRadius: 'var(--radius)',
    border: '1px solid var(--line)',
    background: 'var(--bg)',
    color: 'var(--ink)',
    boxSizing: 'border-box',
  } as const,
  ghost: {
    minHeight: 52,
    padding: '0 14px',
    borderRadius: 'var(--radius)',
    border: '1px solid var(--line)',
    background: 'var(--surface)',
    color: 'var(--ink)',
    fontSize: 15,
    fontWeight: 600,
    cursor: 'pointer',
  } as const,
  note: { fontSize: 13, lineHeight: 1.45, color: 'var(--muted)', margin: '8px 0 0' } as const,
  group: { marginTop: 18 } as const,
  groupHead: {
    fontSize: 12,
    fontWeight: 700,
    color: 'var(--muted)',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    margin: '0 0 4px',
  } as const,
  hit: {
    display: 'flex',
    gap: 10,
    alignItems: 'flex-start',
    width: '100%',
    textAlign: 'left',
    padding: '11px 2px',
    background: 'transparent',
    // `border: none` FIRST. The shorthand after the longhand wipes it, which
    // is how a row separator silently disappears.
    border: 'none',
    borderTop: '1px solid var(--line)',
    color: 'var(--ink)',
    font: 'inherit',
    cursor: 'pointer',
  } as const,
  banner: {
    padding: '10px 12px',
    borderRadius: 'var(--radius)',
    fontSize: 14,
    marginTop: 10,
    background: 'var(--warn-bg)',
    color: 'var(--warn)',
  } as const,
}

/**
 * `snippet()` wraps every match in `[` and `]`. Rendering those brackets raw
 * makes the highlight look like a typo, so they become a mark.
 *
 * A literal bracket in somebody's note will be read as a highlight. That is
 * accepted: the alternative is a second marker convention agreed between two
 * files, and a wrong colour on one word is cheaper than a search that stops
 * highlighting the day somebody changes the SQL.
 */
function Highlighted({ text }: { text: string }) {
  const parts = useMemo(() => text.split(/(\[[^\]]*\])/g), [text])
  return (
    <>
      {parts.map((part, index) =>
        part.startsWith('[') && part.endsWith(']') ? (
          <mark
            key={index}
            style={{ background: 'var(--brand-soft)', color: 'var(--brand-strong)', padding: '0 1px', borderRadius: 3 }}
          >
            {part.slice(1, -1)}
          </mark>
        ) : (
          <span key={index}>{part}</span>
        ),
      )}
    </>
  )
}

/* ────────────────────────────────────────────────────────────────────────────
 * The screen
 * ────────────────────────────────────────────────────────────────────────── */

const DEBOUNCE_MS = 300

export default function Search() {
  const locale = useLocale()
  const t = useCallback((key: CopyKey) => COPY[locale][key] ?? EN[key], [locale])

  const [query, setQuery] = useState('')
  const [result, setResult] = useState<SearchResponse | null>(null)
  const [busy, setBusy] = useState(false)
  const [failed, setFailed] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  // The five payloads `oppCache` is holding. Read once, synchronously, so the
  // empty state has something in it on the first paint even with no signal.
  const [recent] = useState(() =>
    oppCache
      .list()
      .map((id) => {
        const cached = oppCache.get<{ opportunity?: { title?: string | null; ref?: string | null } }>(id)
        return cached
          ? { id, title: cached.payload.opportunity?.title ?? cached.payload.opportunity?.ref ?? id }
          : null
      })
      .filter((row): row is { id: string; title: string } => row !== null),
  )

  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  useEffect(() => {
    const raw = query.trim()
    if (!raw) {
      setResult(null)
      setFailed(false)
      setBusy(false)
      return
    }

    setBusy(true)
    let cancelled = false
    const timer = setTimeout(() => {
      void (async () => {
        try {
          // The RAW string. The server sanitises; see the note at the top.
          const body = await post<{ search: SearchResponse }>('/api/search', { q: raw })
          if (cancelled) return
          setResult(body.search)
          setFailed(false)
        } catch {
          if (cancelled) return
          setResult(null)
          setFailed(true)
        } finally {
          if (!cancelled) setBusy(false)
        }
      })()
    }, DEBOUNCE_MS)

    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [query])

  const openHit = useCallback((hit: SearchHit) => {
    // Everything that belongs to an opportunity opens the opportunity — a note
    // and a call are context, not destinations. A seller with no opportunity
    // attached is the one hit that has nowhere else to go, so it stays put.
    const target = hit.opportunityId ?? (hit.kind === 'opportunity' ? hit.id : null)
    if (target) navigate(`/o/${encodeURIComponent(target)}`)
  }, [])

  const groups = KIND_ORDER.map((kind) => ({
    kind,
    hits: (result?.groups?.[kind] ?? []).slice(),
  })).filter((group) => group.hits.length > 0)

  return (
    <div style={S.page}>
      <div style={S.bar}>
        <input
          ref={inputRef}
          style={S.input}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={t('placeholder')}
          aria-label={t('title')}
          type="search"
          // `inputMode` stays default: the same box takes a phone number and a
          // Spanish place name, and forcing a numeric keypad would break one to
          // help the other.
          autoComplete="off"
          autoCapitalize="off"
          spellCheck={false}
          enterKeyHint="search"
        />
        {query && (
          <button type="button" style={S.ghost} onClick={() => setQuery('')}>
            {t('clear')}
          </button>
        )}
      </div>

      <p style={S.note}>{t('hint')}</p>

      {failed && <div style={S.banner}>{t('failed')}</div>}
      {result?.strategy === 'like' && <div style={S.banner}>{t('degraded')}</div>}

      {busy && <p style={S.note}>{t('searching')}</p>}

      {!query.trim() && recent.length > 0 && (
        <div style={S.group}>
          <h2 style={S.groupHead}>{t('recent')}</h2>
          {recent.map((row) => (
            <button
              key={row.id}
              type="button"
              style={S.hit}
              onClick={() => navigate(`/o/${encodeURIComponent(row.id)}`)}
            >
              <Icon name="clock" size={18} />
              <span style={{ flex: 1, minWidth: 0, fontSize: 15, fontWeight: 600 }}>{row.title}</span>
              <Icon name="chevron" size={16} />
            </button>
          ))}
          <p style={S.note}>{t('recentWhy')}</p>
        </div>
      )}

      {result && !busy && result.hits.length === 0 && (
        <div style={S.group}>
          <p style={{ fontSize: 16, fontWeight: 600, margin: 0 }}>{t('none')}</p>
          <p style={S.note}>{t('noneWhy')}</p>
        </div>
      )}

      {groups.map((group) => (
        <div key={group.kind} style={S.group}>
          <h2 style={S.groupHead}>
            {t(`kind_${group.kind}` as CopyKey)} · {group.hits.length} {t('results')}
          </h2>
          {group.hits.map((hit) => (
            <button key={`${hit.kind}:${hit.id}`} type="button" style={S.hit} onClick={() => openHit(hit)}>
              <span style={{ flex: '0 0 auto', marginTop: 2, color: 'var(--muted)' }}>
                <Icon name={KIND_ICON[hit.kind]} size={18} />
              </span>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: 'block', fontSize: 15, fontWeight: 600 }}>
                  <Highlighted text={hit.title} />
                </span>
                {hit.excerpt && (
                  <span style={{ display: 'block', fontSize: 13, color: 'var(--muted)', lineHeight: 1.4, marginTop: 2 }}>
                    <Highlighted text={hit.excerpt} />
                  </span>
                )}
              </span>
              <span style={{ flex: '0 0 auto', marginTop: 2, color: 'var(--muted)' }}>
                <Icon name="chevron" size={16} />
              </span>
            </button>
          ))}
        </div>
      ))}
    </div>
  )
}
